/**
 * ExternalEventForm.tsx
 *
 * Simplified form for external events — no description, genres, tickets, or age requirement.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  FiMapPin,
  FiCamera,
  FiChevronLeft,
  FiChevronRight,
  FiTrash2,
  FiLink,
  FiClock,
  FiX,
  FiCheck,
  FiPlus,
} from 'react-icons/fi';
import { Button } from '../ui/Button';
import { FormLabel } from '../ui/FormLabel';
import { Input } from '../ui/Input';
import { DateInput } from '../ui/DateInput';
import { TimezoneSelect, DEFAULT_TIMEZONE } from '../ui/TimezoneSelect';
import { CollaboratorsSelector } from '../media/CollaboratorsSelector';
import { LocationSearchInput } from '../common/LocationSearchInput';
import { toast } from '../../utils/toast';
import type { LocationBody } from '../../services/locationService';
import { useDominantColor } from '../../hooks/useDominantColor';
import { useEventAccentColor } from '../../hooks/useEventAccentColor';
import { normalizeColor } from '../../utils/dominantColor';

export interface ExternalEventFormValues {
  event_title: string;
  info?: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  timezone: string;
  locationBody: LocationBody | null;
  locationDetails?: string;
  images: string[];
  /** New local files (parallel to images array; null = existing server URL) */
  imageFiles: (File | null)[];
  /** Server image IDs (parallel to images array; null = new local file) */
  existingImageIds: (number | null)[];
  external_ticketing_url?: string;
  collaboratorIds: number[];
  is_draft_or_published: 'draft' | 'published';
  deletedImageIds?: number[];
  /** Dominant color of the first/primary image, as a "#rrggbb" hex string. */
  dominantColor?: string | null;
}

interface ExternalEventFormProps {
  mode: 'create' | 'edit';
  title: string;
  statusBadge?: 'draft' | 'published';
  secondaryActionLabel?: string;
  onSubmit: (values: ExternalEventFormValues, action: 'save' | 'publish') => void;
  onSecondaryAction?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  initialData?: Partial<ExternalEventFormValues>;
  isLoading?: boolean;
}

const MAX_IMAGES = 5;

export const ExternalEventForm: React.FC<ExternalEventFormProps> = ({
  mode,
  title,
  statusBadge,
  secondaryActionLabel,
  onSubmit,
  onSecondaryAction,
  onDirtyChange,
  initialData,
  isLoading = false,
}) => {
  const [images, setImages] = useState<string[]>(initialData?.images ?? []);
  const [imageFiles, setImageFiles] = useState<(File | null)[]>(initialData?.imageFiles ?? []);
  const [existingImageIds, setExistingImageIds] = useState<(number | null)[]>(
    initialData?.existingImageIds ?? [],
  );
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dominantColor] = useDominantColor(images[0], initialData?.dominantColor ?? null);
  const eventAccent = useEventAccentColor(dominantColor);

  const [eventTitle, setEventTitle] = useState(initialData?.event_title ?? '');
  const [info, setInfo] = useState(initialData?.info ?? '');
  const [externalUrl, setExternalUrl] = useState(initialData?.external_ticketing_url ?? '');

  const [startDate, setStartDate] = useState(initialData?.startDate ?? '');
  const [startTime, setStartTime] = useState(initialData?.startTime ?? '');
  const [endDate, setEndDate] = useState(initialData?.endDate ?? '');
  const [endTime, setEndTime] = useState(initialData?.endTime ?? '');
  const [timezone, setTimezone] = useState(initialData?.timezone ?? DEFAULT_TIMEZONE);

  const [locationBody, setLocationBody] = useState<LocationBody | null>(
    initialData?.locationBody ??
      ((initialData as any)?.venue
        ? ({
            address: (initialData as any).venue,
            city: (initialData as any).city ?? '',
            state: (initialData as any).state ?? '',
            country: (initialData as any).country ?? '',
            latitude: 0,
            longitude: 0,
          } as LocationBody)
        : null),
  );
  const [locationDetails, setLocationDetails] = useState(initialData?.locationDetails ?? '');
  const [showLocationModal, setShowLocationModal] = useState(false);

  const [collaboratorIds, setCollaboratorIds] = useState<number[]>(
    initialData?.collaboratorIds ?? [],
  );

  const isPublishedMode = mode === 'edit' && statusBadge === 'published';

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Dirty tracking ──
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!initialData) {
      setIsDirty(false);
      onDirtyChange?.(false);
      return;
    }
    const current: Partial<ExternalEventFormValues> = {
      event_title: eventTitle,
      info,
      startDate,
      startTime,
      endDate,
      endTime,
      timezone,
      locationBody,
      locationDetails,
      images,
      external_ticketing_url: externalUrl,
      collaboratorIds,
    };
    const initialSnapshot: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(initialData)) {
      if (key in current) initialSnapshot[key] = value;
    }
    (current as Record<string, unknown>).dominantColor = normalizeColor(dominantColor);
    initialSnapshot.dominantColor = normalizeColor(initialData.dominantColor ?? null);
    const dirty = JSON.stringify(current) !== JSON.stringify(initialSnapshot);
    setIsDirty(dirty);
    onDirtyChange?.(dirty);
  }, [
    eventTitle,
    info,
    startDate,
    startTime,
    endDate,
    endTime,
    timezone,
    locationBody,
    locationDetails,
    images,
    dominantColor,
    externalUrl,
    collaboratorIds,
    initialData,
    onDirtyChange,
  ]);

  const validImageTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/svg+xml',
  ];

  const handleAddImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.warn('Only 5 images will be processed.');
      return;
    }
    const toAdd = Array.from(files).slice(0, remaining);
    if (files.length > remaining) {
      toast.warn('Only 5 images will be processed.');
    }
    const invalidFile = toAdd.find((f) => !validImageTypes.includes(f.type));
    if (invalidFile) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP, AVIF, or SVG).');
      // Reset input so the same file can be re-selected
      e.target.value = '';
      return;
    }
    const newUrls = toAdd.map((f) => URL.createObjectURL(f));
    setImages((prev) => {
      const next = [...prev, ...newUrls];
      // Reset selection if we just added the first image(s)
      setSelectedImageIndex((cur) => (cur >= next.length ? 0 : cur));
      return next;
    });
    setImageFiles((prev) => [...prev, ...toAdd]);
    setExistingImageIds((prev) => [...prev, ...toAdd.map(() => null)]);
  };

  const removeImage = (idx: number) => {
    const existingId = existingImageIds[idx];
    if (existingId != null) {
      setDeletedImageIds((prev) => [...prev, existingId]);
    }
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setExistingImageIds((prev) => prev.filter((_, i) => i !== idx));
    setSelectedImageIndex((cur) => {
      if (idx === cur) return 0;
      if (idx < cur) return cur - 1;
      return cur;
    });
  };

  const moveImage = (idx: number, dir: 'left' | 'right') => {
    const swapArr = <T,>(arr: T[]): T[] => {
      const next = [...arr];
      const swapIdx = dir === 'left' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= next.length) return arr;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    };
    setImages((prev) => swapArr(prev));
    setImageFiles((prev) => swapArr(prev));
    setExistingImageIds((prev) => swapArr(prev));
    setSelectedImageIndex((cur) => {
      if (cur === idx) return dir === 'left' ? idx - 1 : idx + 1;
      return cur;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!eventTitle.trim()) newErrors.eventTitle = 'Event title is required';
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
      if (mode !== 'edit' && startDate < todayStr) {
        newErrors.startDate = 'Start date cannot be in the past';
      }
    }
    if (!startTime) newErrors.startTime = 'Start time is required';
    if (!endDate) {
      newErrors.endDate = 'End date is required';
    } else if (startDate && endDate < startDate) {
      newErrors.endDate = 'End date cannot be before start date';
    } else if (startDate && endDate === startDate && startTime && endTime && endTime <= startTime) {
      newErrors.endTime = 'End time must be after start time';
    }
    if (!endTime) newErrors.endTime = 'End time is required';
    if (!locationBody) newErrors.location = 'Event address is required';
    if (!externalUrl.trim()) {
      newErrors.externalUrl = 'External link is required';
    } else {
      try {
        const parsed = new URL(externalUrl);
        if (parsed.protocol !== 'https:') {
          newErrors.externalUrl = 'Link must start with https://';
        }
      } catch {
        newErrors.externalUrl = 'Please enter a valid URL starting with https://';
      }
    }

    // At least one image is required
    if (images.length < 1) {
      newErrors.images = 'Please upload at least one image.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (action: 'save' | 'publish') => {
    if (mode === 'edit' && action === 'save' && !isDirty) {
      // Nothing to save — prevent unnecessary API request.
      return;
    }
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    const values: ExternalEventFormValues = {
      event_title: eventTitle,
      info,
      startDate,
      startTime,
      endDate,
      endTime,
      timezone,
      locationBody,
      locationDetails,
      images,
      imageFiles,
      existingImageIds,
      external_ticketing_url: externalUrl,
      collaboratorIds,
      is_draft_or_published: action === 'publish' ? 'published' : 'draft',
      deletedImageIds,
      dominantColor,
    };
    onSubmit(values, action);
  };

  const inputClass =
    'w-full bg-zinc-900/60 border border-white/8 rounded-2xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/10 transition-all text-[14px]';

  return (
    <div
      className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8"
      style={eventAccent.style}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleAddImages}
      />

      {/* ── Title row ── */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-white text-2xl sm:text-[32px] font-bold">{title}</h1>
          {statusBadge && (
            <span
              className={`px-3 py-1 rounded-full text-[12px] font-semibold ${
                statusBadge === 'published'
                  ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                  : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
              }`}
            >
              {statusBadge === 'published' ? 'Published' : 'Draft'}
            </span>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-3">
          {mode === 'edit' && secondaryActionLabel && onSecondaryAction && (
            <Button
              type="button"
              variant="outline"
              className="rounded-full px-6 border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={onSecondaryAction}
            >
              <FiX size={15} className="mr-1.5" />
              {secondaryActionLabel}
            </Button>
          )}
          {mode === 'create' && (
            <>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full px-6 border border-white/10 text-zinc-300 hover:bg-white/5"
                onClick={() => handleSubmit('save')}
                disabled={isLoading}
              >
                <FiCheck size={15} className="mr-1.5" />
                Save as Draft
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full px-6 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
                onClick={() => handleSubmit('publish')}
                disabled={isLoading}
              >
                <FiPlus size={15} className="mr-1.5" />
                Create
              </Button>
            </>
          )}
          {mode === 'edit' && statusBadge === 'draft' && (
            <>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full px-6 border border-white/10 text-zinc-300 hover:bg-white/5"
                onClick={() => handleSubmit('save')}
                disabled={isLoading || !isDirty}
              >
                <FiCheck size={15} className="mr-1.5" />
                Save as Draft
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full px-6 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
                onClick={() => handleSubmit('publish')}
                disabled={isLoading}
              >
                <FiPlus size={15} className="mr-1.5" />
                Publish
              </Button>
            </>
          )}
          {mode === 'edit' && statusBadge !== 'draft' && (
            <Button
              type="button"
              variant="secondary"
              className="rounded-full px-6 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 disabled:bg-zinc-600 disabled:text-zinc-400"
              onClick={() => handleSubmit('save')}
              disabled={isLoading || !isDirty}
            >
              <FiCheck size={15} className="mr-1.5" />
              Save Changes
            </Button>
          )}
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* ── LEFT COLUMN ── */}
        <div className="space-y-8">
          {/* Image Upload */}
          <div>
            <FormLabel required className="mb-2">
              Event Images
            </FormLabel>
            <div
              className="relative w-full aspect-[16/9] rounded-2xl border-2 border-dashed border-white/10 bg-zinc-900/40 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-white/5 hover:border-white/20 transition-all overflow-hidden"
              onClick={() => images.length < MAX_IMAGES && fileInputRef.current?.click()}
            >
              {images.length > 0 ? (
                <img
                  src={images[selectedImageIndex] ?? images[0]}
                  alt="Cover"
                  className="w-full h-full object-contain"
                />
              ) : (
                <>
                  <FiCamera size={28} className="text-zinc-600" />
                  <p className="text-zinc-500 text-sm font-medium">Add Event Images</p>
                  <p className="text-zinc-600 text-xs">Up to 5 Photos</p>
                </>
              )}
            </div>
            {images.length > 0 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {images.map((src, i) => (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedImageIndex(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedImageIndex(i);
                      }
                    }}
                    className={`relative w-20 h-20 rounded-xl overflow-hidden shrink-0 group cursor-pointer transition-all ${
                      i === selectedImageIndex
                        ? 'ring-2 ring-white border border-transparent'
                        : 'border border-white/10 hover:border-white/30'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(i);
                      }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-red-500 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                    >
                      <FiTrash2 size={10} />
                    </button>
                    {images.length > 1 && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveImage(i, 'left');
                          }}
                          disabled={i === 0}
                          className="w-4 h-4 rounded-full bg-black/60 flex items-center justify-center text-white disabled:opacity-30"
                        >
                          <FiChevronLeft size={8} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveImage(i, 'right');
                          }}
                          disabled={i === images.length - 1}
                          className="w-4 h-4 rounded-full bg-black/60 flex items-center justify-center text-white disabled:opacity-30"
                        >
                          <FiChevronRight size={8} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {images.length < MAX_IMAGES && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center text-zinc-600 hover:text-zinc-400 hover:border-white/20 transition-colors shrink-0"
                  >
                    <FiPlus size={18} />
                  </button>
                )}
              </div>
            )}
            <p className="text-zinc-600 text-xs mt-1.5">Attached photos</p>
            {errors.images && <p className="text-red-400 text-xs mt-1">{errors.images}</p>}
          </div>

          {/* Basic Information */}
          <div className="space-y-6">
            <h2 className="text-white text-lg font-semibold">Basic Information</h2>
            <div>
              <FormLabel required>Title</FormLabel>
              <input
                type="text"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder="Enter Title"
                className={`${inputClass} mt-2`}
              />
              {errors.eventTitle && (
                <p className="text-red-400 text-xs mt-1">{errors.eventTitle}</p>
              )}
            </div>
            <div>
              <FormLabel>Describe your event (Optional)</FormLabel>
              <textarea
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                placeholder="Event description"
                rows={5}
                className={`${inputClass} mt-2 resize-none`}
              />
            </div>
            <div>
              <FormLabel required>Link</FormLabel>
              <div className="relative mt-2">
                <FiLink
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
                />
                <input
                  type="url"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="Enter event link"
                  className={`${inputClass} pl-11`}
                />
              </div>
              {errors.externalUrl && (
                <p className="text-red-400 text-xs mt-1">{errors.externalUrl}</p>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="space-y-8">
          {/* Date & Time */}
          <div className="space-y-5">
            <h2 className="text-white text-lg font-semibold">Date & Time</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel required>Start Date</FormLabel>
                <DateInput
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  className={`h-14 bg-zinc-900/60 ${errors.startDate ? 'border-red-500/60' : 'border-white/8'}`}
                />
                {errors.startDate && (
                  <p className="text-red-400 text-xs mt-1">{errors.startDate}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel required>End Date</FormLabel>
                <DateInput
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  className={`h-14 bg-zinc-900/60 ${errors.endDate ? 'border-red-500/60' : 'border-white/8'}`}
                />
                {errors.endDate && <p className="text-red-400 text-xs mt-1">{errors.endDate}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel required>Start Time</FormLabel>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  placeholder="Start time"
                  icon={FiClock}
                  className="h-14 bg-zinc-900/60 border-white/8 scheme-dark"
                />
                {errors.startTime && (
                  <p className="text-red-400 text-xs mt-1">{errors.startTime}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel required>End Time</FormLabel>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  placeholder="End time"
                  icon={FiClock}
                  className="h-14 bg-zinc-900/60 border-white/8 scheme-dark"
                />
                {errors.endTime && <p className="text-red-400 text-xs mt-1">{errors.endTime}</p>}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <FormLabel required>Timezone</FormLabel>
              <TimezoneSelect value={timezone} onChange={setTimezone} />
            </div>

            {/* Location */}
            <div className="space-y-5">
              <h2 className="text-white text-lg font-semibold">Location</h2>

              <div>
                <FormLabel required>Event Address</FormLabel>
                <button
                  type="button"
                  onClick={() => setShowLocationModal(true)}
                  className={`w-full flex items-center gap-3 mt-2 px-4 py-3.5 rounded-2xl border transition-all text-left ${
                    locationBody
                      ? 'bg-zinc-900/60 border-white/8 text-white'
                      : 'bg-zinc-900/60 border-white/8 text-zinc-600'
                  }`}
                >
                  <FiMapPin
                    size={18}
                    className={locationBody ? 'text-blue-400' : 'text-zinc-500'}
                  />
                  <span className="text-[14px] truncate">
                    {locationBody
                      ? [locationBody.city, locationBody.state, locationBody.country]
                          .filter(Boolean)
                          .join(', ') || locationBody.address
                      : 'Select location'}
                  </span>
                </button>
                {locationBody && (
                  <p className="text-zinc-600 text-xs mt-1.5 text-right">
                    {
                      (
                        [locationBody.city, locationBody.state, locationBody.country]
                          .filter(Boolean)
                          .join(', ') || locationBody.address
                      ).length
                    }
                    /200
                  </p>
                )}
                {errors.location && <p className="text-red-400 text-xs mt-1">{errors.location}</p>}
              </div>

              <div>
                <FormLabel>Location Details (Optional)</FormLabel>
                <div className="relative mt-2">
                  <FiMapPin size={16} className="absolute left-4 top-3.5 text-blue-400 shrink-0" />
                  <textarea
                    value={locationDetails}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) {
                        setLocationDetails(e.target.value);
                      }
                    }}
                    placeholder="Enter Location Details (Optional)..."
                    rows={3}
                    className={`${inputClass} pl-11 resize-none`}
                  />
                </div>
                <p className="text-zinc-600 text-xs mt-1.5 text-right">
                  {locationDetails.length}/200
                </p>
              </div>
            </div>
          </div>

          {/* Collaborators */}
          {!isPublishedMode && (
            <div>
              <CollaboratorsSelector
                searchOnly
                selectedIds={collaboratorIds}
                onToggle={(id) =>
                  setCollaboratorIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile buttons */}
      <div className="flex sm:hidden flex-col gap-3 pt-8 mt-8 border-t border-white/5">
        {mode === 'create' && (
          <>
            <Button
              type="button"
              variant="ghost"
              className="w-full rounded-full h-14 border border-white/10 text-zinc-300"
              onClick={() => handleSubmit('save')}
              disabled={isLoading}
            >
              <FiCheck size={16} className="mr-2" />
              Save as Draft
            </Button>
            <Button
              type="button"
              className="w-full rounded-full h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
              onClick={() => handleSubmit('publish')}
              disabled={isLoading}
            >
              <FiPlus size={16} className="mr-2" />
              Create
            </Button>
          </>
        )}
        {mode === 'edit' && statusBadge === 'draft' && (
          <>
            <Button
              type="button"
              variant="ghost"
              className="w-full rounded-full h-14 border border-white/10 text-zinc-300"
              onClick={() => handleSubmit('save')}
              disabled={isLoading || !isDirty}
            >
              <FiCheck size={16} className="mr-2" />
              Save as Draft
            </Button>
            <Button
              type="button"
              className="w-full rounded-full h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95"
              onClick={() => handleSubmit('publish')}
              disabled={isLoading}
            >
              <FiPlus size={16} className="mr-2" />
              Publish
            </Button>
          </>
        )}
        {mode === 'edit' && statusBadge !== 'draft' && (
          <Button
            type="button"
            className="w-full rounded-full h-14 bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 disabled:bg-zinc-700 disabled:text-zinc-400"
            onClick={() => handleSubmit('save')}
            disabled={isLoading || !isDirty}
          >
            <FiCheck size={16} className="mr-2" />
            Save Changes
          </Button>
        )}
        {mode === 'edit' && secondaryActionLabel && onSecondaryAction && (
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full h-14 border-red-500/30 text-red-400"
            onClick={onSecondaryAction}
          >
            <FiX size={16} className="mr-2" />
            {secondaryActionLabel}
          </Button>
        )}
      </div>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-0 sm:px-4">
          <div className="w-full sm:max-w-md bg-zinc-950 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-semibold">Select Location</h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="p-2 rounded-full hover:bg-white/5 text-zinc-400"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <LocationSearchInput
                initialLocation={locationBody}
                onLocationSelected={(loc) => {
                  setLocationBody(loc);
                  if (loc) setShowLocationModal(false);
                }}
                placeholder="Search city, address..."
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
